import PropTypes from "prop-types";
import { useCallback, useMemo, useRef, useState } from "react";
import { CloseIcon, SearchIcon } from "./Icons.jsx";
import s from "./Toolbar.module.css";

export default function SearchCombobox({
  searchQuery,
  setSearchQuery,
  characters,
  vaList,
  setActiveCharacter,
  setActiveVa,
  handleClearAll,
  filterLabels,
}) {
  const [focused, setFocused] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const q = searchQuery.toLowerCase().trim();

  const suggestions = useMemo(() => {
    if (!q) return [];
    const results = [];

    // Match characters
    if (characters) {
      for (const c of characters) {
        if (
          c.name.toLowerCase().includes(q) ||
          c.vas.some((v) => v.toLowerCase().includes(q))
        ) {
          results.push({
            type: "character",
            name: c.name,
            meta: c.vas.join(", "),
            count: c.count,
          });
        }
        if (results.length >= 8) break;
      }
    }

    // Match talent/VAs
    if (vaList && results.length < 8) {
      for (const v of vaList) {
        if (
          v.name.toLowerCase().includes(q) ||
          v.chars.some((c) => c.toLowerCase().includes(q))
        ) {
          // Avoid duplicating if already shown as character
          if (
            !results.some((r) => r.type === "character" && r.name === v.name)
          ) {
            results.push({
              type: "talent",
              name: v.name,
              meta: v.chars.join(", "),
              count: v.count,
            });
          }
        }
        if (results.length >= 8) break;
      }
    }

    return results;
  }, [q, characters, vaList]);

  const showDropdown = focused && q.length > 0 && suggestions.length > 0;

  const selectSuggestion = useCallback(
    (suggestion) => {
      if (suggestion.type === "character") {
        setActiveCharacter(suggestion.name);
      } else {
        setActiveVa(suggestion.name);
      }
      setSearchQuery("");
      setHighlightIdx(-1);
      inputRef.current?.blur();
    },
    [setActiveCharacter, setActiveVa, setSearchQuery],
  );

  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === "Enter" && highlightIdx >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIdx]);
    } else if (e.key === "Escape") {
      inputRef.current?.blur();
    }
  };

  const activeLabel = filterLabels.length > 0 ? filterLabels.join(", ") : null;
  const showInput = focused || searchQuery || !activeLabel;

  return (
    <div className={s.searchWrap}>
      <div className={s.searchField}>
        <SearchIcon />
        {showInput ? (
          <input
            ref={inputRef}
            className={s.searchInput}
            type="text"
            placeholder="Search products, characters..."
            aria-label="Search products by name or character"
            aria-expanded={showDropdown}
            aria-controls="search-suggestions"
            aria-activedescendant={
              highlightIdx >= 0 ? `suggestion-${highlightIdx}` : undefined
            }
            role="combobox"
            aria-autocomplete="list"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setHighlightIdx(-1);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              // Delay to allow click on suggestion
              setTimeout(() => setFocused(false), 150);
            }}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <button
            className={s.searchLabelBtn}
            onClick={() => {
              setFocused(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
          >
            <span className={s.searchValue}>{activeLabel}</span>
          </button>
        )}
        {(searchQuery || activeLabel) && (
          <button
            className={s.searchClear}
            onClick={() => {
              if (searchQuery) {
                setSearchQuery("");
              } else {
                handleClearAll();
              }
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {showDropdown && (
        <ul
          id="search-suggestions"
          ref={listRef}
          className={s.suggestions}
          role="listbox"
        >
          {suggestions.map((item, idx) => (
            <li
              key={`${item.type}-${item.name}`}
              id={`suggestion-${idx}`}
              role="option"
              aria-selected={highlightIdx === idx}
              className={`${s.suggestion}${
                highlightIdx === idx ? ` ${s.suggestionHighlight}` : ""
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(item);
              }}
            >
              <span className={s.suggestionName}>{item.name}</span>
              {item.meta && (
                <span className={s.suggestionMeta}>{item.meta}</span>
              )}
              <span className={s.suggestionBadge}>
                {item.type === "character" ? "Character" : "Talent"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

SearchCombobox.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  characters: PropTypes.array,
  vaList: PropTypes.array,
  setActiveCharacter: PropTypes.func.isRequired,
  setActiveVa: PropTypes.func.isRequired,
  handleClearAll: PropTypes.func.isRequired,
  filterLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
};
